import React from 'react';
import { useTranslation } from 'react-i18next';
import { Award, Target, Eye, Calendar, MapPin, Users, CheckCircle, Sparkles, Zap, Globe, Shield } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { motion } from 'framer-motion';

const About = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const timelineEvents = [
    {
      year: '1999',
      title: t('about.founded'),
      description: 'Global Technology was established with a vision to provide comprehensive engineering solutions.'
    },
    {
      year: '2005',
      title: 'Regional Expansion',
      description: 'Expanded operations across the Middle East region, establishing key partnerships.'
    },
    {
      year: '2010',
      title: 'International Growth',
      description: 'Opened offices in USA and strengthened global presence.'
    },
    {
      year: '2013',
      title: t('about.expansion'),
      description: 'Completed expansion to multiple countries and achieved ISO certifications.'
    },
    {
      year: '2020',
      title: 'Digital Transformation',
      description: 'Embraced digital technologies and enhanced service delivery capabilities.'
    },
    {
      year: '2024',
      title: 'Continued Excellence',
      description: 'Maintaining leadership in engineering solutions with cutting-edge innovations.'
    }
  ];

  const certifications = [
    {
      title: t('about.iso9001'),
      description: 'Quality Management System certification ensuring consistent quality delivery.',
      icon: Award,
      color: 'from-global-green to-global-dark-blue'
    },
    {
      title: t('about.iso14001'),
      description: 'Environmental Management System certification for sustainable operations.',
      icon: Award,
      color: 'from-global-green to-global-dark-blue'
    },
    {
      title: t('about.iso45001'),
      description: 'Occupational Health & Safety Management System certification.',
      icon: Award,
      color: 'from-global-green to-global-dark-blue'
    }
  ];

  const stats = [
    { number: '25+', label: 'Years of Experience', icon: Calendar },
    { number: '3', label: 'Global Offices', icon: MapPin },
    { number: '500+', label: 'Projects Completed', icon: CheckCircle },
    { number: '100+', label: 'Team Members', icon: Users }
  ];

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden text-white h-[70vh]">
        {/* Background Image */}
       <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('/divisions/General/dubai-downtown-skyline-united-arab-emirates-or-ua-2024-10-18-11-06-55-utc.jpg')`
          }}
        ></div>
        
        {/* Futuristic Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-global-green to-global-light-blue/80"></div>
        
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute top-20 right-20 w-40 h-40 bg-global-light-blue/20 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.7, 0.3],
              rotate: [0, 180, 360]
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-20 left-20 w-32 h-32 bg-global-green/20 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.4, 1],
              opacity: [0.2, 0.6, 0.2],
              rotate: [360, 180, 0]
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 3
            }}
          />
          
          {/* Floating particles */}
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-white/30 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [-20, -100, -20],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>
        
        <div className="container-max relative z-10">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <motion.div
              className="pb-2 inline-flex items-center gap-2 mb-6 px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <Sparkles className="w-4 h-4 text-global-light-blue" />
              <span className="text-white text-sm font-medium">
                Since 1999 • Engineering Excellence
              </span>
              <Sparkles className="w-4 h-4 text-global-light-blue" />
            </motion.div>
            
            <motion.h1 
              className="pt-8 text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
            >
              <span className="block pb-2">About Global Technology</span>
              <motion.span 
                className="pb-5 block text-global-light-blue"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1 }}
              >
              </motion.span>
            </motion.h1>
            
            <motion.p 
              className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed text-gray-200"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.7 }}
            >
              Excellence in Engineering Solutions
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="section-padding relative bg-white overflow-hidden">
        <div className="container-max relative z-10">
          <div className="text-center mb-16">
            
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-6">
              Our Story
            </h2>
            <p className="text-lg text-slate max-w-3xl mx-auto">
              Discover how we've grown from a vision to a global leader in engineering solutions.
            </p>
          </div>

          {/* Stats Parallel Layout */}
          <div className="space-y-12 mb-16">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div key={index} className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 lg:gap-16`}>
                  {/* Image/Icon Section */}
                  <div className="flex-shrink-0 w-full lg:w-1/2">
                    <div className="relative">
                      <div className="aspect-square max-w-sm mx-auto bg-gradient-to-r from-global-green to-global-light-blue rounded-3xl p-12 flex items-center justify-center shadow-lg">
                        <Icon className="w-24 h-24 text-white" />
                      </div>
                      {/* Decorative elements */}
                      <div className="absolute -top-4 -right-4 w-8 h-8 bg-global-light-blue rounded-full opacity-60"></div>
                      <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-global-green rounded-full opacity-40"></div>
                    </div>
                  </div>
                  
                  {/* Description Section */}
                  <div className="flex-1 w-full lg:w-1/2 text-center lg:text-left">
                    <div className="max-w-lg mx-auto lg:mx-0">
                      <div className="text-6xl lg:text-7xl font-bold text-charcoal mb-4 leading-none">
                        {stat.number}
                      </div>
                      
                      <h3 className="text-2xl lg:text-3xl font-bold text-charcoal mb-4">
                        {stat.label}
                      </h3>
                      
                      <div className="w-16 h-1 bg-gradient-to-r from-global-green to-global-light-blue mx-auto lg:mx-0 mb-4"></div>
                      
                      <p className="text-lg text-slate leading-relaxed">
                        {stat.label === 'Years of Experience' && 'Over two decades of delivering exceptional engineering solutions across diverse industries worldwide.'}
                        {stat.label === 'Global Offices' && 'Strategic locations enabling us to serve clients efficiently across multiple continents and time zones.'}
                        {stat.label === 'Projects Completed' && 'Successfully delivered projects ranging from small-scale installations to large industrial complexes.'}
                        {stat.label === 'Team Members' && 'Skilled professionals including engineers, technicians, and specialists committed to excellence.'}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Description */}
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-charcoal mb-6">
              Excellence Through Innovation
            </h3>
            <p className="text-lg text-slate mb-6 leading-relaxed">
              {t('about.overview')}
            </p>
            <p className="text-lg text-slate leading-relaxed">
              With a commitment to excellence and innovation, we have built lasting relationships with clients across diverse industries, delivering solutions that drive success and create value.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-soft-gray relative overflow-hidden">
        <div className="container-max relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-charcoal mb-6">
              Mission & Vision
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-global-green to-global-light-blue mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Mission Card */}
            <div className="relative">
              <div className="bg-white p-10 rounded-3xl border border-slate/20">
                <div className="mb-8">
                  <div className="w-20 h-20 bg-gradient-to-r from-global-green to-global-light-blue rounded-2xl flex items-center justify-center">
                    <Target className="w-10 h-10 text-white" />
                  </div>
                </div>
                
                <h3 className="text-3xl font-bold text-charcoal mb-6">
                  Our Mission
                </h3>
                
                <p className="text-lg text-slate leading-relaxed mb-6">
                  {t('about.mission')}
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-global-green mt-1 flex-shrink-0" />
                    <span className="text-slate">Deliver innovative engineering solutions</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-global-green mt-1 flex-shrink-0" />
                    <span className="text-slate">Maintain highest quality standards</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-global-green mt-1 flex-shrink-0" />
                    <span className="text-slate">Foster long-term partnerships</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Vision Card */}
            <div className="relative">
              <div className="bg-white p-10 rounded-3xl border border-slate/20">
                <div className="mb-8">
                  <div className="w-20 h-20 bg-gradient-to-r from-global-green to-global-light-blue rounded-2xl flex items-center justify-center">
                    <Eye className="w-10 h-10 text-white" />
                  </div>
                </div>
                
                <h3 className="text-3xl font-bold text-charcoal mb-6">
                  Our Vision
                </h3>
                
                <p className="text-lg text-slate leading-relaxed mb-6">
                  To be the leading global provider of engineering solutions, recognized for our expertise, quality, and to excellence.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-6 h-6 text-global-light-blue mt-1 flex-shrink-0" />
                    <span className="text-slate">Global leadership in engineering</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-6 h-6 text-global-light-blue mt-1 flex-shrink-0" />
                    <span className="text-slate">Sustainable technology solutions</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-6 h-6 text-global-light-blue mt-1 flex-shrink-0" />
                    <span className="text-slate">Innovation-driven growth</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="container-max relative z-10">
          <div className="text-center mb-20">
            <div className="inline-block mb-4">
              <span className="px-4 py-2 bg-global-green/10 text-global-green rounded-full text-sm font-medium">
                Our Journey
              </span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-charcoal mb-6">
              Company Timeline
            </h2>
            <p className="text-lg text-slate max-w-3xl mx-auto">
              Milestones that shaped our journey to becoming a global leader in engineering solutions.
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-global-green rounded-full"></div>
            
            <div className="space-y-16">
              {timelineEvents.map((event, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-12 text-right' : 'pl-12 text-left'}`}>
                    <div className="bg-soft-gray p-8 rounded-2xl border border-slate/20">
                      <div className="text-3xl font-bold text-global-dark-blue mb-2">
                        {event.year}
                      </div>
                      <h3 className="text-xl font-bold text-charcoal mb-4">
                        {event.title}
                      </h3>
                      <p className="text-slate leading-relaxed">
                        {event.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Timeline Node */}
                  <div className="relative z-10">
                    <div className="w-6 h-6 bg-global-light-blue rounded-full border-4 border-white"></div>
                  </div>
                  
                  <div className="w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section - Futuristic Enhanced */}
      <section className="section-padding relative overflow-hidden">
        {/* Futuristic Background */}
        
        

        <div className="container-max relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 relative">
              <span className="text-global-green">
                Certifications & Standards
              </span>
              <div className="absolute -inset-1 bg-global-green/20 blur-xl opacity-30 animate-pulse"></div>
            </h2>
            <p className="text-lg text-global-light-blue/80 max-w-3xl mx-auto">
              Our commitment to excellence is validated through internationally recognized certifications and quality standards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {certifications.map((cert, index) => {
              const Icon = cert.icon;
              return (
                <div 
                  key={index} 
                  className="relative group"
                  style={{
                    animation: `fadeInUp 0.8s ease-out ${index * 0.2}s both`
                  }}
                >
                  
                  {/* Main Card */}
                  <Card className="relative bg-gradient-to-br from-slate-800/80 to-charcoal/80 border-0 p-8 rounded-3xl h-full backdrop-blur-sm group-hover:transform group-hover:scale-105 transition-all duration-500">
                    {/* Inner Glow */}
                    <div className="absolute inset-0 bg-gradient-to-br from-global-green/5 to-global-dark-blue/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    
                    <CardHeader className="text-center pb-6 relative z-10">
                      {/* Enhanced Icon Container */}
                      <div className="relative w-24 h-24 mx-auto mb-6 group-hover:transform  transition-transform duration-500">
                        <div className="absolute inset-0 bg-gradient-to-br from-global-green to-global-dark-blue rounded-3xl animate-pulse"></div>
                        
                        <div className="absolute inset-1 bg-gradient-to-br from-global-green to-global-dark-blue rounded-3xl flex items-center justify-center">
                          <Icon className="w-12 h-12 text-white drop-shadow-lg" />
                        </div>
                        
                
                      </div>
                      
                      <CardTitle className="text-xl font-bold text-black mb-2 group-hover:text-global-light-blue transition-colors duration-300">
                        {cert.title}
                      </CardTitle>
                    </CardHeader>
                    
                    <CardContent className="relative z-10">
                      <CardDescription className="text-global-light-blue/70 leading-relaxed group-hover:text-global-light-blue/90 transition-colors duration-300">
                        {cert.description}
                      </CardDescription>
                      
                      {/* Progress Bar Effect */}
                      <div className="mt-6 h-1 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-global-green to-global-light-blue rounded-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000 ease-out"
                        
                        ></div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>

        {/* Custom CSS Animations */}
      </section>

      {/* Values Section */}
      <section className="section-padding bg-white relative overflow-hidden">
        {/* Futuristic Background Elements */}
        <div className="absolute inset-0">
          
          {/* Floating Particles */}
          <div className="absolute inset-0">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 bg-global-light-blue rounded-full opacity-30"
                style={{
                  left: `${20 + i * 15}%`,
                  top: `${30 + (i % 3) * 20}%`,
                  animation: `float ${3 + i * 0.5}s ease-in-out infinite ${i * 0.5}s`
                }}
              ></div>
            ))}
          </div>
        </div>

        <div className="container-max relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-charcoal mb-6 animate-fadeInUp">
              Our Core Values
            </h2>
            <div className="w-24 h-1 bg-global-green mx-auto rounded-full mb-6 animate-expandWidth"></div>
            <p className="text-lg text-slate max-w-3xl mx-auto animate-fadeInUp" >
              The principles that guide our actions and define our culture.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Target, title: 'Excellence', description: 'Striving for the highest standards in everything we do.' },
              { icon: Users, title: 'Integrity', description: 'Building trust through honest and ethical practices.' },
              { icon: Zap, title: 'Innovation', description: 'Embracing new technologies and creative solutions.' },
              { icon: Globe, title: 'Collaboration', description: 'Working together to achieve shared success.' }
            ].map((value, index) => {
              const Icon = value.icon;
              return (
                <div 
                  key={index} 
                  className="text-center group animate-slideInUp"
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  {/* Enhanced Icon Container with Futuristic Effects */}
                  <div className="relative mb-6 mx-auto w-fit">
                    {/* Outer Glow Ring */}
                    <div className="absolute inset-0 w-20 h-20 mx-auto rounded-2xl bg-global-light-blue/20 scale-110 group-hover:scale-125 transition-transform duration-500 animate-pulse"></div>
                    
                    {/* Main Icon Container */}
                    <div className="relative w-20 h-20 bg-gradient-to-r from-global-green to-global-light-blue rounded-2xl flex items-center justify-center mx-auto group-hover:rotate-12 transition-all duration-500 shadow-lg group-hover:shadow-2xl">
                      {/* Inner Glow */}
                      <div className="absolute inset-2 bg-white/20 rounded-xl"></div>
                      
                      
                      
                      <Icon className="w-10 h-10 text-white relative z-10 group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    
                    {/* Floating Accent Dots */}
                    <div className="absolute -top-2 -right-2 w-3 h-3 bg-global-green rounded-full opacity-60 group-hover:animate-bounce"></div>
                    <div className="absolute -bottom-2 -left-2 w-2 h-2 bg-global-light-blue rounded-full opacity-40 group-hover:animate-pulse"></div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-charcoal mb-4 group-hover:text-global-dark-blue transition-colors duration-300">
                    {value.title}
                  </h3>
                  <p className="text-slate leading-relaxed group-hover:text-charcoal transition-colors duration-300">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Custom CSS Animations */}
        <style jsx>{`
          @keyframes gridMove {
            0% { transform: translate(0, 0); }
            100% { transform: translate(50px, 50px); }
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(180deg); }
          }
          
          @keyframes fadeInUp {
            0% { opacity: 0; transform: translateY(30px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          
          @keyframes slideInUp {
            0% { opacity: 0; transform: translateY(50px) scale(0.9); }
            100% { opacity: 1; transform: translateY(0) scale(1); }
          }
          
          @keyframes expandWidth {
            0% { width: 0; opacity: 0; }
            100% { width: 6rem; opacity: 1; }
          }
          
          @keyframes borderSpin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .animate-fadeInUp {
            animation: fadeInUp 0.8s ease-out forwards;
            opacity: 0;
          }
          
          .animate-slideInUp {
            animation: slideInUp 0.8s ease-out forwards;
            opacity: 0;
          }
          
          .animate-expandWidth {
            animation: expandWidth 1.2s ease-out forwards 0.5s;
            width: 0;
          }
          
          .animate-borderSpin {
            animation: borderSpin 3s linear infinite;
          }
        `}</style>
      </section>
    </div>
  );
};

export default About;